import { cx } from '@flexbyt/widget-preview';
import { useEffect, useRef, useState } from 'react';

/**
 * The reviewer's own photo — one picture, shown as the circle it becomes.
 *
 * Separate from `PhotoPicker`, which collects pictures of the *experience*:
 * this one is who said it, drawn beside their name and role in the widget, and
 * a testimonial wall of initials in coloured discs is a much weaker wall than
 * one with faces. Optional always — nobody should be stopped from leaving a
 * review because they would rather not share their face.
 */
export function HeadshotPicker({
  value,
  onChange,
  label,
}: {
  value: File | null;
  onChange: (file: File | null) => void;
  label: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  return (
    <div className="mt-4 flex items-center gap-3 text-left">
      <button
        type="button"
        onClick={() => input.current?.click()}
        className={cx(
          'grid size-14 shrink-0 place-items-center overflow-hidden rounded-full text-xl text-ink-400',
          'border border-dashed border-black/20 bg-white transition-colors',
          'hover:border-black/40 hover:text-ink-600',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600'
        )}
      >
        {preview ? (
          <img src={preview} alt="" className="size-full object-cover" />
        ) : (
          <span aria-hidden>+</span>
        )}
        <span className="sr-only">{label}</span>
      </button>
      <div className="min-w-0">
        <p className="text-micro font-medium text-[#55595f]">{label}</p>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="mt-0.5 text-micro text-ink-500 underline-offset-2 hover:underline"
          >
            Remove
          </button>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          onChange(event.target.files?.[0] ?? null);
          if (input.current) input.current.value = '';
        }}
      />
    </div>
  );
}
